import React, { useEffect, useRef, useState } from 'react';
import { Icon, Input, Label, Modal, Table } from 'semantic-ui-react';
import TimeAgo from 'react-timeago';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';
import { notifError, notifSuccess } from 'components/Tools/NotificationToasts';
import frenchStrings from '../Tools/TimeAgoFrStrings';
import { torrentHistory, torrentRedownload } from '../../services/Torrent';
import { getDownloadHistory } from '../../services/Search';

const formatter = buildFormatter(frenchStrings);

function CloudHistoryModal({ setOpen, open, setSearchInput, focusSearchInput }) {
  const [state, setState] = useState({
    history: [],
    error: false,
    searchQuery: '',
    loading: false,
  });

  const debounceRef = useRef(null);
  const searchGenRef = useRef(0);

  const getDisplayName = (file) => {
    if (!file) return file;
    const magnetMatch = file.match(/[?&]dn=([^&]+)/);
    if (magnetMatch) {
      try {
        const cleaned = magnetMatch[1].replace(/\.torrent$/i, '').replace(/%[0-9a-fA-F]?$/, '');
        return decodeURIComponent(cleaned.replace(/\+/g, ' '));
      } catch (_) {
        return file;
      }
    }
    return file.replace(/\.torrent$/i, '');
  };

  const deduplicateByHash = (items) =>
    items.filter(
      (res, index, arr) => !res.hash || arr.findIndex((r) => r.hash === res.hash) === index
    );

  const searchHistory = (query) => {
    searchGenRef.current += 1;
    const generation = searchGenRef.current;
    setState((prev) => ({ ...prev, loading: true, error: false }));
    getDownloadHistory(query)
      .then((response) => {
        if (generation !== searchGenRef.current) return;
        if (response.status === 200 && response.data.success) {
          setState((prev) => ({
            ...prev,
            loading: false,
            history: deduplicateByHash(response.data.result).map((item) => ({
              ...item,
              file: getDisplayName(item.file),
            })),
          }));
        } else {
          setState((prev) => ({ ...prev, loading: false, error: true }));
        }
      })
      .catch(() => {
        if (generation !== searchGenRef.current) return;
        setState((prev) => ({ ...prev, loading: false, error: true }));
      });
  };

  const getTorrentHistory = () => {
    searchGenRef.current += 1;
    const generation = searchGenRef.current;
    setState((prev) => ({ ...prev, loading: true, error: false }));
    torrentHistory()
      .then((response) => {
        if (generation !== searchGenRef.current) return;
        if (response.status === 200 && response.data.success) {
          setState((prev) => ({
            ...prev,
            loading: false,
            history: response.data.result.map((item) => ({
              ...item,
              file: getDisplayName(item.file),
            })),
            error: false,
          }));
        } else {
          setState((prev) => ({ ...prev, loading: false, history: [], error: true }));
        }
      })
      .catch(() => {
        if (generation !== searchGenRef.current) return;
        setState((prev) => ({ ...prev, loading: false, history: [], error: true }));
      });
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setState((prev) => ({ ...prev, searchQuery: value }));
    clearTimeout(debounceRef.current);
    if (value) {
      debounceRef.current = setTimeout(() => {
        searchHistory(value);
      }, 200);
    } else {
      getTorrentHistory();
    }
  };

  const reDownload = (downloader_id, file) => {
    torrentRedownload({ downloader_id, file })
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          notifSuccess('Téléchargement démarré');
        } else notifError(`Échec: ${response.data.message}`);
      })
      .catch((e) => {
        notifError(`Échec du téléchargement ${e.message}`);
      });
  };

  useEffect(() => {
    getTorrentHistory();
    return () => {
      clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <Modal
      closeIcon
      size="large"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
    >
      <Modal.Header>
        <Icon name="history" /> Historique Cloud
      </Modal.Header>
      <Modal.Content>
        <Input
          fluid
          icon="search"
          iconPosition="left"
          placeholder="Filtrer l'historique..."
          value={state.searchQuery}
          onChange={handleSearchChange}
          loading={state.loading}
          style={{ marginBottom: '1em' }}
        />
      </Modal.Content>
      <Modal.Content scrolling>
        {state.error ? (
          <p>
            Erreur lors de la récupération de l&apos;historique
            <br />
            Veuillez réessayer ultérieurement
          </p>
        ) : (
          <Table unstackable selectable>
            <Table.Body>
              {state.history.map((item) => {
                return (
                  <Table.Row
                    key={`cloud-history-item-${item.downloader_id + encodeURI(item.file)}`}
                  >
                    <Table.Cell style={{ wordBreak: 'break-all' }}>
                      {item.file}
                      <br />
                      <Label color="blue" size="small" ribbon>
                        {item.user.nickname}
                      </Label>
                      <Label color="teal" size="small" basic>
                        <TimeAgo date={item.updated_at} formatter={formatter} />
                      </Label>{' '}
                      <Label color="grey" size="small" basic>
                        {item.count} fois
                      </Label>{' '}
                      {(item.magnet || item.torrent_path) && (
                        <Label
                          className="clickable"
                          color="orange"
                          size="small"
                          onClick={() => reDownload(item.downloader_id, item.file)}
                        >
                          <Icon name="cloud download" />
                          Re-télécharger
                        </Label>
                      )}
                      <Label
                        className="clickable"
                        color="green"
                        size="small"
                        onClick={() => {
                          setOpen(false);
                          setSearchInput(
                            item.file.replace('Lien magnet', '').replace('.torrent', '').trim()
                          );
                          focusSearchInput();
                        }}
                      >
                        <Icon name="search" />
                        Chercher
                      </Label>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}
      </Modal.Content>
    </Modal>
  );
}

export default CloudHistoryModal;
